import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { MapPin, User, CalendarDays, Clock, LineSquiggle, Eye } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

type Props = {
  routeId: string | undefined
}


interface StopPoint {
  id: number;
  route_id: number;
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  stop_order: number;
  estimated_arrival_time: number;
}

interface Route {
  id: number;
  name: string;
  description?: string;
  start_location: string;
  end_location: string;
  start_latitude: number;
  start_longitude: number;
  end_latitude: number;
  end_longitude: number;
  distance_km: number;
  estimated_duration_minutes: number;
  route_type: string;
  is_public: boolean;
  created_by: number;
  creator_first_name?: string;
  creator_last_name?: string;
  created_at: string;
  updated_at: string;
  stop_points?: StopPoint[];
  average_rating?: number;
  total_ratings?: number;
}

const RouteDetails: React.FC<Props> = ({ routeId }) => {
  const [route, setRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoute();
  }, [routeId]);

  const fetchRoute = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/routes/${routeId}?include_stops=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch route');
      }

      const data = await response.json();
      setRoute(data.route);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch route');
    } finally {
      setLoading(false);
    }
  };


  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} minutes`;
  };


  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !route) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'Route not found'}</div>
        <button
          onClick={() => navigate('/routes')}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Back to Routes
        </button>
      </div>
    );
  }

  return (

    <>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{route.name}</h1>
            {route.description && (
              <p className="text-gray-600 mb-4">{route.description}</p>
            )}
          </div>
          <div className="text-right">
            <div className="flex items-center text-sm text-gray-500">
              <Eye className="h-4 w-4 mr-1" />
              {route.is_public ? 'Public' : 'Private'}
            </div>
          </div>
        </div>

        {/* Route Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">From</div>
              <div className="font-medium">{route.start_location}</div>
            </div>
          </div>
          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">To</div>
              <div className="font-medium">{route.end_location}</div>
            </div>
          </div>
          <div className="flex items-center">

            <LineSquiggle className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Distance</div>
              <div className="font-medium">{route.distance_km} km</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-purple-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Duration</div>
              <div className="font-medium">{formatDuration(route.estimated_duration_minutes)}</div>
            </div>
          </div>
          <div className="flex items-center">
            <User className="h-5 w-5 text-orange-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Created by</div>
              <div className="font-medium">
                {[route.creator_first_name, route.creator_last_name].filter(Boolean).join(' ') || 'Unknown'}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <CalendarDays className="h-5 w-5 text-gray-600 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Created</div>
              <div className="font-medium">{formatDate(route.created_at)}</div>
            </div>
          </div>
        </div>
      </div>
    </>


  )
}

export default RouteDetails